<template>
  <el-form
    :model="insurance"
    :rules="rules"
    :ref="formName"
    label-position="top"
    class="upload-data"
    autocomplete="off"
    :data-blockName="blockName"
    v-loading.fullscreen.lock="fullscreenLoading"
  >
    <div class="wrap">
      <div
        class="description"
        @click="showMore = !showMore"
      >
        <div class="info-icon">
          <i
            class="fa fa-info-circle"
            aria-hidden="true"
          />
        </div>
        <div
          :class="[{'empty' : !isLoadedData && !isUpdateData, 'fill' : isLoadedData || isUpdateData, 'show-more' : showMore}, 'text']"
        >
          <span>
            {{ $t('preCheckIn.insuranceCard.types.' + type.toLowerCase()) }} {{ $t('common.labels.insurance.name').toLowerCase() }}
          </span>
          {{ contentData }}
        </div>
      </div>
      <div
        class="empty-data"
        v-if="!isLoadedData && !isUpdateData"
      >
        <div
          :class="[{'active' : active, 'loaded' : isUpdateData}, 'photo-wrap']"
        >
          <div class="front">
            <span
              class="is-mobile row"
              :label="$t('common.labels.side.label', { side: $t('common.labels.side.front.name').toUpperCase(), type: $t('components.form.insuranceBlock.label') })"
            />
            <div
              class="block"
            >
              <el-form-item
                :label="$t('common.labels.side.label', { side: $t('common.labels.side.front.name').toUpperCase(), type: $t('components.form.insuranceBlock.label') })"
                prop="frontSide"
                class="document-wrap"
                v-if="typeof insurance.frontSide !== 'undefined'"
              >
                <div
                  class="no-image"
                  v-show="!insurance.frontSide"
                >
                  <i class="fa fa-camera" />
                </div>
                <div
                  class="upload-title"
                  v-show="!insurance.frontSide"
                >
                  <i18n
                    path="common.labels.side.title"
                  >
                    <template v-slot:side>
                      <b>{{ $t('common.labels.side.front.name') }}</b>
                    </template>
                    <template v-slot:type>
                      {{ $t('components.form.insuranceBlock.label') }}
                    </template>
                  </i18n>
                </div>
                <PreUploader
                  :base64.sync="insurance.frontSide"
                  @imageUploaded="frontSideUploaded"
                  :class="{'loaded-front': insurance.frontSide}"
                />
              </el-form-item>
            </div>
          </div>
          <div class="back">
            <span
              class="is-mobile row"
              :label="$t('common.labels.side.label', { side: $t('common.labels.side.back.name').toUpperCase(), type: $t('components.form.insuranceBlock.label') })"
            />
            <div
              class="block"
              v-if="insurance"
            >
              <el-form-item
                :label="$t('common.labels.side.label', { side: $t('common.labels.side.back.name').toUpperCase(), type: $t('components.form.insuranceBlock.label') })"
                prop="backSide"
                class="document-wrap"
                v-if="typeof insurance.backSide !== 'undefined'"
              >
                <div
                  class="no-image"
                  v-show="!insurance.backSide"
                >
                  <i class="fa fa-camera" />
                </div>
                <div
                  class="upload-title"
                  v-show="!insurance.backSide"
                >
                  <i18n
                    path="common.labels.side.title"
                  >
                    <template v-slot:side>
                      <b>{{ $t('common.labels.side.back.name') }}</b>
                    </template>
                    <template v-slot:type>
                      {{ $t('components.form.insuranceBlock.label') }}
                    </template>
                  </i18n>
                </div>
                <PreUploader
                  :base64.sync="insurance.backSide"
                  @imageUploaded="backSideUploaded"
                  :class="{'loaded-back': insurance.backSide}"
                />
              </el-form-item>
            </div>
          </div>
        </div>
        <div
          :class="[{'active' : active}, 'data-wrap']"
        >
          <el-row class="block">
            <el-row class="data full">
              <el-form-item
                :label="(!isES) ? $t('common.labels.insurance.name') + ' ' + $t('common.labels.insurance.company') : $t('common.labels.insurance.company') + ' ' + $t('common.labels.insurance.name')"
                prop="planCode"
                :class="[{'focus': insurance.planCode}, 'left', 'required-field']"
              >
                <el-select
                  filterable
                  v-model="insurance.planCode"
                  @change="selectInsurance($event)"
                >
                  <el-option
                    v-for="item in insurances"
                    :key="item.code"
                    :label="item.name"
                    :value="item.code"
                  />
                </el-select>
              </el-form-item>
            </el-row>
            <el-row class="data">
              <el-form-item
                :label="(!isES) ? $t('common.labels.insurance.name') + ' ' + $t('common.labels.memberId') : $t('common.labels.memberId')"
                prop="memberId"
                :class="[{'focus': insurance.memberId}, 'required-field', {'lng': isES}]"
              >
                <el-input
                  type="text"
                  v-model="insurance.memberId"
                  :value="insurance.memberId"
                />
              </el-form-item>
              <el-form-item
                :label="$t('common.labels.insurance.groupNumber')"
                prop="groupNumber"
                :class="{'focus': insurance.groupNumber}"
              >
                <el-input
                  type="text"
                  v-model="insurance.groupNumber"
                  :value="insurance.groupNumber"
                />
              </el-form-item>
            </el-row>
            <el-row class="data">
              <el-form-item
                :label="$t('common.labels.insurance.startDate')"
                prop="startDate"
                :class="[{'focus': insurance.startDate}, 'date']"
              >
                <el-date-picker
                  v-model="insurance.startDate"
                  type="date"
                  v-mask="'##/##/####'"
                  format="MM/dd/yyyy"
                  value-format="yyyy-MM-dd"
                  :picker-options="pickerOptions"
                />
              </el-form-item>
              <el-form-item
                :label="$t('common.labels.insurance.endDate')"
                prop="endDate"
                :class="[{'focus': insurance.endDate}, 'date']"
              >
                <el-date-picker
                  v-model="insurance.endDate"
                  type="date"
                  v-mask="'##/##/####'"
                  format="MM/dd/yyyy"
                  value-format="yyyy-MM-dd"
                  :picker-options="pickerOptions"
                />
              </el-form-item>
            </el-row>
          </el-row>
        </div>
      </div>
      <div
        class="filled-data"
        v-if="isLoadedData || isUpdateData"
      >
        <div class="images">
          <div class="front">
            <el-form-item
              class="label"
              :label="$t('common.labels.side.label', { side: $t('common.labels.side.front.name').toUpperCase(), type: $t('components.form.insuranceBlock.label') })"
            >
              <img
                v-if="typeof insurance.frontSide !== 'undefined'"
                :src="insurance.frontSide"
                class="image"
              >
            </el-form-item>
          </div>
          <div class="back">
            <el-form-item
              class="label"
              :label="$t('common.labels.side.label', { side: $t('common.labels.side.back.name').toUpperCase(), type: $t('components.form.insuranceBlock.label') })"
            >
              <img
                v-if="typeof insurance.backSide !== 'undefined'"
                :src="insurance.backSide"
                class="image"
              >
            </el-form-item>
          </div>
        </div>
        <div class="insurance-data">
          <div class="row-wrap">
            <div
              class="elem"
              v-if="insurance.startDate"
            >
              <div>{{ $t('common.labels.insurance.startDate') }}:</div>
              <div>{{ toFormat(insurance.startDate) }}</div>
            </div>
            <div
              class="elem"
              v-if="insurance.endDate"
            >
              <div>{{ $t('common.labels.insurance.endDate') }}:</div>
              <div>{{ toFormat(insurance.endDate) }}</div>
            </div>
            <div
              class="elem"
              v-if="typeof insurance.name !== 'undefined'"
            >
              <div>{{ $t('common.labels.insurance.plan') }}:</div>
              <div>{{ insurance.name }}</div>
            </div>
            <div
              class="elem"
              v-if="insurance.memberId"
            >
              <div>{{ $t('common.labels.memberId') }}:</div>
              <div>{{ insurance.memberId }}</div>
            </div>
            <div
              class="elem"
              v-if="insurance.groupNumber"
            >
              <div>{{ $t('common.labels.insurance.groupNumber') }}:</div>
              <div>{{ insurance.groupNumber }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <el-form-item
      class="save-container"
      v-show="active"
    >
      <el-form-item class="save-btn">
        <el-button
          type="primary"
          v-if="!isLoadedData && !isUpdateData"
          @click="saveData(formName)"
        >
          {{ $t('common.buttons.save') }}
        </el-button>
        <div
          v-if="isLoadedData || isUpdateData"
          :class="{'secondary' : isSecondaryBtn}"
        >
          <div
            class="add-buttons"
            v-if="isPrimaryBtn || isSecondaryBtn"
          >
            <el-button
              type="primary"
              v-if="isPrimaryBtn"
              @click="confirmInsuranceSwap(type, 'Primary')"
            >
              {{ $t('common.buttons.setAs', { type: $t('preCheckIn.insuranceCard.types.primary') }) }}
            </el-button>
            <el-button
              type="secondary"
              v-if="isSecondaryBtn"
              @click="confirmInsuranceSwap(type, 'Secondary')"
            >
              {{ $t('common.buttons.setAs', { type: $t('preCheckIn.insuranceCard.types.secondary') }) }}
            </el-button>
          </div>
          <el-button
            type="secondary"
            @click="editData"
            class="edit"
          >
            {{ $t('common.buttons.edit') }}
          </el-button>
        </div>
      </el-form-item>
    </el-form-item>
    <el-row
      class="confirm-swap"
      v-if="confirmSwap"
    >
      <div class="popup">
        <div class="popup-overlay" />
        <div class=" popup-content">
          <p class="title">
            {{ confirmSwapMsg }}
          </p>
          <div class="submit-wrapper save-btn">
            <el-button
              type="secondary"
              @click="cancelSwapInsurance()"
            >
              {{ $t('common.buttons.cancel') }}
            </el-button>
            <el-button
              type="primary"
              @click="swapInsurance()"
            >
              {{ $t('common.buttons.confirm') }}
            </el-button>
          </div>
        </div>
      </div>
    </el-row>
  </el-form>
</template>

<script>
import PreUploader from '../../form/PreUploader';
import store from "../../../store";
import moment from 'moment';

export default {
  name: 'InsuranceBlock',
  components: {
    PreUploader
  },
  data: () => {
    return {
      user: {},
      fullscreenLoading: false,
      isFrontLoaded: false,
      isBackLoaded: false,
      loadedData: false,
      initInsurances: {},
      patientNumber: null,
      activeStep: null,
      isUpdateData: false,
      formName: '',
      blockName: '',
      confirmSwap: false,
      confirmSwapMsg: '',
      swapFrom: '',
      swapTo: '',
      showMore: false,
      isES: false,
      sides: [
        'front',
        'back',
      ],
      sid: null,
      primaryInsuranceFrontUploadCount: 0,
      primaryInsuranceBackUploadCount: 0,
      secondaryInsuranceFrontUploadCount: 0,
      secondaryInsuranceBackUploadCount: 0,
      tertiaryInsuranceFrontUploadCount: 0,
      tertiaryInsuranceBackUploadCount: 0,
      statInsuranceFieldName: '',
      routerName: 'InsuranceInfo',
      rules: {},
      fields: ['planCode', 'memberId'],
      pickerOptions: {
        disabledDate (time) {
          return time.getTime() < new Date('01/01/2000').getTime();
        }
      }
    };
  },
  props: {
    insurance: {
      type: Object, default: () => {
        return {};
      }
    },
    insurances: {
      type: Array, default: () => {
        return [];
      }
    },
    contentData: {
      type: String, default: () => {
        return '';
      }
    },
    type: {
      type: String, default: () => {
        return '';
      }
    },
    active: {
      type: Boolean, default: () => {
        return false;
      }
    },
    isLoadedData: {
      type: Boolean, default: () => {
        return false;
      }
    },
    isPrimaryBtn: {
      type: Boolean, default: () => {
        return false;
      }
    },
    isSecondaryBtn: {
      type: Boolean, default: () => {
        return false;
      }
    }
  },
  watch: {
    'insurance.planCode': function (val) {
      let newValue = ''
      this.insurances.map((item) => {
        if (item.code == val) newValue =  item.name;
      });
      this.changeField({[this.statInsuranceFieldName]: newValue});
    },
    'insurance.memberId': function (val) {
      this.changeField({[this.statInsuranceFieldName + '_memberId']: val});
    },
    'insurance.groupNumber': function (val) {
      this.changeField({[this.statInsuranceFieldName + '_groupNumber']: val});
    },
    'insurance.startDate': function (val) {
      this.changeField({[this.statInsuranceFieldName + '_startDate']: this.formattedDate(val)});
    },
    'insurance.endDate': function (val) {
      this.changeField({[this.statInsuranceFieldName + '_endDate']: this.formattedDate(val)});
    },
  },
  created () {
    this.fullscreenLoading = true;
    let pathData = window.location.pathname.replace(/^\/|\/$/g, '').split('/');
    if (pathData[0] == 'es') this.isES = true
    this.formName = 'insuranceCardForm' + this.type;
    this.blockName = this.type.toLowerCase() + 'InsuranceBlock';
    this.setProgressBar();
    this.setRules(this.fields);
    this.setImageRules(this.sides, 'insurance');
    this.activeStep = this.$route.path.substring(1).split('/')[1];
    this.patientNumber = store.getters.patientData.patientNumber;
    this.sid = this.getSessionId();
    this.statInsuranceFieldName = this.type.toLowerCase()+'Insurance';
    if(!this.patientNumber) {
      this.getDataByPatientNumber().then(() => {
        this.patientNumber = store.getters.patientData.patientNumber;
        this.fullscreenLoading = false;
      }).catch(() => {
        this.fullscreenLoading = false;
        this.setMessage(this.$t('common.errors.fail'));
      });
    } else {
      this.fullscreenLoading = false;
    }
  },
  methods: {
    goNext() {
      this.$emit('nextStep');
    },
    validate(){
      this.fullscreenLoading = true;
      this.$refs[this.formName].validate((valid) => {
        if (valid) {
          let savedInsurance = store.getters.insurancesData[this.type.toLowerCase() + 'Insurance'];
          if (!this.isObjEmpty(savedInsurance) && Object.prototype.hasOwnProperty.call(savedInsurance, 'planId')) {
            this.goNext();
          } else {
            this.saveInsuranceCard(this.insurance, this.type, true);
          }
        } else {
          this.$emit('scroll');
          this.fullscreenLoading = false;
        }
      });
    },
    confirmInsuranceSwap(from, to){
      this.confirmSwapMsg = this.$t('preCheckIn.insuranceCard.confirmSwap.start') + ' ' +
          this.insurance.name + ' ' +
          this.$t('preCheckIn.insuranceCard.confirmSwap.end', {type: this.$t('preCheckIn.insuranceCard.types.' + to.toLowerCase()).toLowerCase()});
      this.$set(this, 'swapFrom', from);
      this.$set(this, 'swapTo', to);
      this.confirmSwap = true;
    },
    cancelSwapInsurance(){
      this.confirmSwap = false;
    },
    swapInsurance() {
      let nameFrom = this.swapFrom.toLowerCase() + 'Insurance',
          nameTo = this.swapTo.toLowerCase() + 'Insurance',
          insurancesData = store.getters.insurancesData;

      let arr = [
        { nameFrom: nameFrom, to: this.swapTo, nameTo : nameTo },
        { nameFrom: nameTo, to: this.swapFrom, nameTo : nameFrom }
      ];

      this.saveStatisticData(this.sid, {
        [nameFrom + '_swap']: 'as' + this.swapTo
      });

      this.confirmSwap = false;
      this.fullscreenLoading = true;

      const promises = [];
      arr.forEach((item) => {
        promises.push(
          new Promise((resolve) => {
            this.storePatientInsurance(this.patientNumber, insurancesData[item.nameFrom], item.to).then((response) => {
              if (response.data.isSuccess) {
                this.saveInsuranceCardImage(insurancesData[item.nameFrom], 'frontSide', 'true').then((response) => {
                  if (response.data.isSuccess) {
                    this.saveStatisticData(this.sid, {
                      [this.statInsuranceFieldName + '_front_save']: 'true'
                    });
                    this.saveInsuranceCardImage(insurancesData[item.nameFrom],'backSide', false).then((response) => {
                      if (response.data.isSuccess) {
                        this.saveStatisticData(this.sid, {
                          [this.statInsuranceFieldName + '_back_save']: 'true'
                        });
                        let data = insurancesData[item.nameFrom];
                        this.updateUserAppData(store.getters.patientData.patientId, {
                          [item.nameTo]: {
                            planCode: data.planCode,
                            groupNumber: parseInt(data.groupNumber) || '',
                            memberId: data.memberId || '',
                            startDate: data.startDate || '',
                            endDate: data.endDate || ''
                          }
                        })

                        this.saveStatisticData(this.sid, {
                          [this.statInsuranceFieldName + '_save']: 'true'
                        });

                        resolve({type: item.nameTo, data: insurancesData[item.nameFrom]});
                      } else {
                        this.fullscreenLoading = false;
                        this.setMessage(this.$t('common.errors.update', { type: this.$t('preCheckIn.insuranceCard.types.' + item.to) + this.$t('common.labels.insurance.name').toLowerCase() }), 'warning');
                      }
                    });
                  } else {
                    this.fullscreenLoading = false;
                    this.setMessage(this.$t('common.errors.update', { type: this.$t('preCheckIn.insuranceCard.types.' + item.to) + this.$t('common.labels.insurance.name').toLowerCase() }), 'warning');
                  }
                });
              }
            }).catch(() => {
              this.fullscreenLoading = false;
              this.setMessage(this.$t('common.errors.fail'));
            });
          })
        );
      });

      Promise.all(promises).then((values) => {
        values.forEach((item) => {
          store.dispatch('setInsurancesData', {type: item.type, params: item.data});
        })
        this.$emit('render', [this.swapTo.toLowerCase(), this.type.toLowerCase()]);
        this.fullscreenLoading = false;
      });

    },
    toFormat(element) {
      return moment(element).format('M/D/Y');
    },
    editData() {
      this.$set(this, 'isUpdateData', false);
      this.$emit('update');
    },
    selectInsurance(code) {
      this.insurances.map((item) => {
        if (item.code == code) this.insurance.name =  item.name;
      });
    },
    saveData (formName) {
      this.fullscreenLoading = true;
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.saveInsuranceCard(this.insurance, this.type);
        } else {
          this.fullscreenLoading = false;
          return false;
        }
      });
    },
    async saveInsuranceCard(insurance, type, next = false) {
      this.fullscreenLoading = true;
      try {
        await this.updateStepsData(store.getters.patientData.patientId, {[this.activeStep] : 1})
      } catch {
        this.fullscreenLoading = false;
        this.setMessage(this.$t('common.errors.fail'));
        return
      }

      this.storePatientInsurance(this.patientNumber, insurance, type).then((patientResponse) => {
        if (patientResponse.data.isSuccess) {
          this.saveInsuranceCardImage(insurance, 'frontSide', 'true').then((response) => {
            if (response.data.isSuccess) {
              this.saveStatisticData(this.sid, {
                [`${this.statInsuranceFieldName}_front_save`]: 'true'
              });
              this.saveInsuranceCardImage(insurance, 'backSide', false).then((response) => {
                if (response.data.isSuccess) {
                  this.saveStatisticData(this.sid, {
                    [`${this.statInsuranceFieldName}_back_save`]: 'true'
                  });
                  this.isUpdateData = true;

                  this.$emit('passInsuranceData', {
                    insuranceName: this.insurance.name,
                    insuranceType: this.type
                  });

                  const type = `${this.type.toLowerCase()}Insurance`;

                  store.dispatch('setInsurancesData', {
                    type: type,
                    params: {
                      ...this.insurance,
                      ...patientResponse.data.data
                    }
                  });
                  this.updateUserAppData(store.getters.patientData.patientId, {
                    [type]: {
                      planCode: insurance.planCode,
                      groupNumber: Number.parseInt(insurance.groupNumber) || '',
                      memberId: insurance.memberId || '',
                      startDate: insurance.startDate || '',
                      endDate: insurance.endDate || ''
                    }
                  })

                  this.saveStatisticData(this.sid, {
                    [`${this.statInsuranceFieldName}_save`]: 'true'
                  });

                  if (next) { this.goNext() }
                  this.fullscreenLoading = false;
                } else {
                  this.fullscreenLoading = false;
                  this.setMessage(this.$t('common.errors.save', { type: this.$t('components.form.insuranceBlock.imageLabel') }), 'warning');
                }
              });
            } else {
              this.fullscreenLoading = false;
              this.setMessage(this.$t('common.errors.save', { type: this.$t('components.form.insuranceBlock.imageLabel') }), 'warning');
            }
          });
        }
      }).catch(() => {
        this.fullscreenLoading = false;
        this.setMessage(this.$t('common.errors.fail'));
      });
    },
    saveInsuranceCardImage(insurance, side, isFront) {
      return this.$axios('/Insurance/SaveInsuranceImage', {
        method: 'PUT',
        data: {
          planCode: insurance.planCode,
          patientNumber: this.patientNumber,
          blobData: insurance[side].split(',')[1],
          isFrontImage: isFront
        }
      }).catch(() => {
        this.fullscreenLoading = false;
        this.setMessage(this.$t('common.errors.fail'));
      });
    },
    storePatientInsurance(patientNumber = '', insurance, type) {
      let data = {
        patientNumber: patientNumber,
        practiceId: 0,
        insurance: {
          planCode: insurance.planCode,
          groupNumber: parseInt(insurance.groupNumber) || '',
          memberId: insurance.memberId || '',
          isPrimary: false,
          isSecondary: false,
          isTertiary: false,
          isNew: true,
          startDate: insurance.startDate,
          endDate: insurance.endDate,
          taskDescription: ''
        },
        userName: 'webreg'
      };
      this.$set(data.insurance, 'is'+ type, true);
      return this.$axios('/Insurance/StoreInsurance', {
        method: 'PUT',
        data: data
      });
    },
    frontSideUploaded() {
      this.fullscreenLoading = true;
      this.isFrontLoaded = true;
      this.$forceUpdate();
      this.$refs[this.formName].validateField('frontSide');
      this.$axios('/Ocr/insurance', {
        method: 'POST',
        data: {
          blobData: this.insurance.frontSide.split(',')[1]
        }
      }).then((response) => {
        if (response.data.isSuccess) {
          const patientInsurance = {};
          response.data.data.fields.map((field) => {
            this.$set(patientInsurance, [this.camelize(field.name)], (field.name === 'GroupNumber') ? parseInt(field.value) || '' : field.value);
          });
          if (!this.isObjEmpty(patientInsurance)) {
            if (typeof patientInsurance.memberId !== 'undefined' && patientInsurance.memberId.toString().length) {
              this.$set(this.insurance, 'memberId', patientInsurance.memberId);
            }
            if (typeof patientInsurance.groupNumber !== 'undefined' && patientInsurance.groupNumber.toString().length) {
              this.$set(this.insurance, 'groupNumber', patientInsurance.groupNumber);
            }
          }
          this.fullscreenLoading = false;
        } else {
          this.fullscreenLoading = false;
          this.setMessage(this.$t('common.errors.notRecognize', { type: this.$t('components.form.insuranceBlock.label').toLowerCase() }), 'warning');
        }
        this[[this.type.toLowerCase() + 'InsuranceFrontUploadCount']]++
        this.saveStatisticData(this.sid, {
          [this.statInsuranceFieldName + '_front_upload']: this[[this.type.toLowerCase() + 'InsuranceFrontUploadCount']]
        });
      }).catch(() => {
        this.fullscreenLoading = false;
        this.setMessage(this.$t('common.errors.fail'));
      });
    },
    backSideUploaded() {
      this[[this.type.toLowerCase() + 'InsuranceBackUploadCount']]++
      this.saveStatisticData(this.sid, {
        [this.statInsuranceFieldName + '_back_upload']: this[[this.type.toLowerCase() + 'InsuranceBackUploadCount']]
      });
      this.$emit('fullscreenLoading', false);
      this.isBackLoaded = true;
      this.$forceUpdate();
      this.$refs[this.formName].validateField('backSide');
    }
  }
};
</script>
